import { useQueryClient } from '@tanstack/react-query';
// Family details — Phase 1c (2026-10-03).
//
// Opens after a family is selected or created. Lists all beneficiaries
// (ordered account-holder-first) and offers a thin "Add beneficiary" form
// that looks up the patient by phone or creates one on the fly. The family
// card row on patient_cards is auto-created by addBeneficiary so there's
// no extra step — the receptionist just captures phone + name +
// relationship.
//
// Clicking a beneficiary row opens their patient registration page so the
// receptionist can fill Sections A-F for that individual (clinical history
// stays per-patient, not shared across the family).

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { FamilyBeneficiary, PatientFamily } from '@pamosi/shared';
import { ApiError, receptionApi } from '../lib/api';

const PHONE_RE = /^\+[1-9]\d{7,14}$/;

export function FamilyDetailsScreen() {
    const { id: familyId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [family, setFamily] = useState<PatientFamily | null>(null);
    const [beneficiaries, setBeneficiaries] = useState<FamilyBeneficiary[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [phone, setPhone] = useState('');
    const [fullName, setFullName] = useState('');
    const [relationship, setRelationship] = useState('');
    const [isAccountHolder, setIsAccountHolder] = useState(false);
    const [adding, setAdding] = useState(false);
    const [addError, setAddError] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        if (!familyId) return;
        setLoading(true);
        setLoadError(null);
        try {
            const res = await receptionApi.getFamilyBundle(familyId);
            setFamily(res.family);
            setBeneficiaries(res.beneficiaries);
        } catch (err) {
            setLoadError(err instanceof ApiError ? err.message || err.code : 'Load failed.');
        } finally {
            setLoading(false);
        }
    }, [familyId]);

    useEffect(() => { void refresh(); }, [refresh]);

    async function addBeneficiary() {
        setAddError(null);
        if (!familyId) return;
        if (!PHONE_RE.test(phone)) {
            setAddError('Enter a valid phone in E.164 format (e.g. +2348012345678).'); return;
        }
        if (fullName.trim().length === 0) {
            setAddError('Enter the beneficiary\'s full name.'); return;
        }
        const alreadyHolder = beneficiaries.some((b) => b.is_account_holder);
        if (isAccountHolder && alreadyHolder) {
            setAddError('This family already has an account holder. Uncheck account holder.'); return;
        }
        setAdding(true);
        try {
            await receptionApi.addBeneficiary(familyId, {
                phoneE164: phone,
                fullName: fullName.trim(),
                ...(relationship.trim() ? { relationship: relationship.trim() } : {}),
                ...(isAccountHolder ? { isAccountHolder: true } : {}),
            });
            void queryClient.invalidateQueries({ queryKey: ['clinic-patients'] });
            setPhone(''); setFullName(''); setRelationship(''); setIsAccountHolder(false);
            await refresh();
        } catch (err) {
            if (err instanceof ApiError) {
                if (err.code === 'account_holder_exists') setAddError('This family already has an account holder.');
                else if (err.code === 'insufficient_role') setAddError('Your role can\'t add beneficiaries.');
                else setAddError(err.message || err.code);
            } else setAddError('Add failed. Try again.');
        } finally {
            setAdding(false);
        }
    }

    if (loading) {
        return <div className="text-slate-500 text-sm">Loading family…</div>;
    }
    if (loadError || !family) {
        return (
            <div className="space-y-3">
                <button type="button"
                    className="text-slate-500 text-sm hover:text-slate-700"
                    onClick={() => navigate('/reception')}
                >← Back to Reception</button>
                <div className="pm-card p-4 border-rose-200 bg-rose-50 text-rose-800 text-sm">
                    {loadError ?? 'Family not found.'}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <button type="button"
                    className="text-slate-500 text-sm hover:text-slate-700"
                    onClick={() => navigate('/reception/card/family')}
                >← Back to Family search</button>
                <h1 className="text-slate-900 text-2xl font-semibold mt-2">{family.family_name}</h1>
                <p className="text-slate-500 text-sm mt-1">
                    Family card · {family.account_number ? `#${family.account_number}` : 'no account number'}
                    {family.main_contact_phone_e164 ? ` · ${family.main_contact_phone_e164}` : ''}
                </p>
            </div>

            {/* Beneficiaries */}
            <div className="pm-card p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <div className="text-slate-700 text-sm font-semibold">
                        Beneficiaries ({beneficiaries.length})
                    </div>
                </div>
                {beneficiaries.length === 0 && (
                    <div className="text-slate-500 text-sm">
                        No beneficiaries yet. Add the account holder first below.
                    </div>
                )}
                {beneficiaries.length > 0 && (
                    <ul className="space-y-2">
                        {beneficiaries.map((b) => (
                            <li key={b.id}>
                                <button type="button"
                                    onClick={() => navigate(`/reception/patient/${b.patient_id}`)}
                                    className="pm-card p-3 w-full text-left hover:border-slate-300 transition"
                                >
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="text-slate-900 font-medium truncate">
                                                {b.patient_full_name || '(No name)'}
                                                {b.is_account_holder && (
                                                    <span className="ml-2 text-xs font-normal text-brand-700 bg-brand-50 px-2 py-0.5 rounded">
                                                        Account holder
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-slate-500 text-xs mt-0.5">
                                                {b.patient_phone_e164}
                                                {b.relationship ? ` · ${b.relationship}` : ''}
                                                {b.patient_dob ? ` · DOB ${b.patient_dob}` : ''}
                                            </div>
                                        </div>
                                        <div className="text-slate-400 text-xs">Open →</div>
                                    </div>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Add beneficiary */}
            <div className="pm-card p-4 space-y-3">
                <div className="text-slate-700 text-sm font-semibold">Add beneficiary</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="block">
                        <span className="text-slate-500 text-xs">Phone (E.164)</span>
                        <input type="tel" className="pm-input mt-1" placeholder="+2348012345678"
                            value={phone} onChange={(e) => setPhone(e.target.value.trim())} disabled={adding} />
                    </label>
                    <label className="block">
                        <span className="text-slate-500 text-xs">Full name</span>
                        <input className="pm-input mt-1" placeholder="Jane Adeyemi"
                            value={fullName} onChange={(e) => setFullName(e.target.value)} disabled={adding} />
                    </label>
                    <label className="block">
                        <span className="text-slate-500 text-xs">Relationship <span className="text-slate-400">(optional)</span></span>
                        <input className="pm-input mt-1" placeholder="spouse / child / parent"
                            value={relationship} onChange={(e) => setRelationship(e.target.value)} disabled={adding} />
                    </label>
                    <label className="flex items-center gap-2 mt-6">
                        <input type="checkbox" checked={isAccountHolder}
                            onChange={(e) => setIsAccountHolder(e.target.checked)} disabled={adding} />
                        <span className="text-slate-700 text-sm">Account holder</span>
                    </label>
                </div>
                {addError && <div className="text-rose-700 text-xs">{addError}</div>}
                <button type="button"
                    className="pm-btn-primary w-full"
                    disabled={adding}
                    onClick={() => void addBeneficiary()}
                >{adding ? 'Adding…' : 'Add beneficiary'}</button>
            </div>
        </div>
    );
}
